<script lang="ts">
  /**
   * La fiche, en quatre écrans.
   *
   * La maquette mobile découpe la fiche en onglets plutôt qu'en une page qui
   * déroule : à table, on ouvre l'application pour **une** chose — lancer un
   * dé, noter un Statut, retrouver un objet — et fouiller une page de deux
   * mètres pour la trouver est le contraire de ce qu'on veut. Le bandeau de
   * vitaux, lui, reste visible d'un écran à l'autre.
   *
   * # Il n'y a pas d'écran Combat
   *
   * Il a existé — encaisser un coup, se défendre, frapper — et il a été retiré
   * avec le suivi du tour par tour. Ce qui compte à table lui a survécu et se
   * trouve ailleurs : le tiroir de jet lance les Attaques et les Réactions
   * défensives, et l'écran Santé porte ce qui reste après le combat, Statuts et
   * blessures comprises. Un onglet de moins à traverser.
   */
  import { enregistrerCompteurs, lire, type FicheEnBase } from '../donnees/personnages'
  import {
    ajouterAffliction,
    chiffrerUneAffliction,
    renommerAffliction,
    reprendreUnObjetLibre,
    renommerObjetLibre,
    annoterObjetLibre,
    corrigerUnChiffreDAffliction,
    corrigerUnChiffreDObjet,
    effacerUnChiffreDAffliction,
    chiffrerUnObjetLibre,
    effacerUnChiffreDObjet,
    type CibleEcrite,
    ajusterCompteur,
    acheterUneAugmentation,
    acheterUneCapacite,
    acheterUneMaitrise,
    promouvoir as franchirUnRang,
    basculerNarratif as basculerUnNarratif,
    basculerGreffe as basculerUneGreffe,
    accomplirLeSacrement as accomplirUnSacrement,
    ajouterUneMachine,
    retirerUneMachine,
    renommerUneMachine,
    abimerUneMachine,
    basculerEquipementDeMachine,
    acheterUneMain,
    forgerUneMain,
    forgerUneMainLibre,
    renommerUneMain,
    chargerUneMain,
    defaireUneMain,
    apprivoiser as apprivoiserUnAnimal,
    instruireLeCompagnon,
    ecrireUnRituel,
    effacerUnRituel,
    designerUnFavori,
    choisirUnElement,
    jouerUneCarte,
    racheterUneCarte,
    nouvelleJournee,
    basculerFolie,
    rangerObjet,
    type Rangement,
    ajouterObjet,
    ajouterObjetLibre,
    poserUnRenfort,
    tenterUneAmelioration,
    attribuerUnePropriete,
    basculerAmelioration,
    ajusterLaBourse,
    retirerObjet,
    tenterUneReparation,
    basculerStatut,
    fiche as calculer,
    retirerAffliction,
    type Compteur as Cible,
    type PersonnageEnregistre,
  } from '../rules'
  import { aller } from '../routage'
  import TiroirDeJet from '../composants/TiroirDeJet.svelte'
    import Jets from './fiche/Jets.svelte'
  import Perso from './fiche/Perso.svelte'
  import Sac from './fiche/Sac.svelte'
  import Sante from './fiche/Sante.svelte'

  let { id }: { id: string } = $props()

  const ECRANS = [
    { id: 'jets', nom: 'Jets' },
    { id: 'sante', nom: 'Santé' },
    { id: 'perso', nom: 'Perso' },
    { id: 'sac', nom: 'Sac' },
  ] as const

  type EcranId = (typeof ECRANS)[number]['id']

  let ecran = $state<EcranId>('sante')

  /**
   * Le tiroir de jet vit ici, et non dans l'onglet Jets.
   *
   * C'est ce qui lui permet de rester ouvert par-dessus la Santé ou le Sac :
   * on prépare un Test, on va vérifier une blessure, on revient lancer le dé
   * sans avoir rien perdu (ADR-017).
   */
  let jetEnCours = $state<string | null>(null)
  /** Déclaré une fois pour la scène : une Confrontation dure plusieurs jets. */
  let confrontation = $state(false)
  /** L'usage déclaré, gardé lui aussi d'un jet à l'autre : on attaque
      rarement une seule fois. */
  let typeDeTest = $state<string | null>(null)
  /** Incrémenté à chaque jet, pour que le journal se relise. */
  let jetsFaits = $state(0)
  /** Mesurée, pour que le tiroir se pose au-dessus de la barre d'onglets. */
  let hauteurBarre = $state(0)

  /** `undefined` tant que la base répond, `null` si le personnage n'existe plus. */
  let enBase = $state<FicheEnBase | null | undefined>(undefined)

  $effect(() => {
    void lire(id).then((f) => (enBase = f ?? null))
  })

  /**
   * Tout est recalculé ici, à chaque ouverture, depuis les seules valeurs
   * brutes enregistrées. Rien de ce qui suit n'a été stocké.
   */
  const vue = $derived(enBase ? calculer(enBase.personnage) : null)

  const c = $derived(vue?.compteurs)

  /**
   * Envoie un delta au moteur, garde ce qu'il rend, l'écrit.
   *
   * L'écran ne borne rien : il dit « −3 » et le domaine décide si cela fait
   * tomber en État critique, ou si le soin s'arrête au maximum.
   */
  async function ajuster(cible: Cible, delta: number) {
    if (!enBase || delta === 0) return
    const personnage = ajusterCompteur(enBase.personnage, cible, delta)
    enBase = { ...enBase, personnage }
    await enregistrerCompteurs(enBase.id, personnage)
  }

  /**
   * Garde ce que le moteur rend, et l'écrit.
   *
   * Statuts, blessures et Afflictions passent tous par là : c'est toujours le
   * même geste — le domaine tranche, la base enregistre.
   */
  async function poser(fait: (p: PersonnageEnregistre) => PersonnageEnregistre) {
    if (!enBase) return
    const personnage = fait(enBase.personnage)
    enBase = { ...enBase, personnage }
    await enregistrerCompteurs(enBase.id, personnage)
  }

  /**
   * Encaisse un coup, et laisse le moteur en tirer les conséquences.
   *
   * Trois d'un coup : les PV tombent, la blessure s'ouvre si l'Attaque était
   * visée, l'armure touchée s'abîme si le coup était critique. C'est le livre
   * qui enchaîne, pas l'écran.
   */
  const gestes = {
    statut: (id: string) => poser((p) => basculerStatut(p, id)),
    folie: (id: string) => poser((p) => basculerFolie(p, id)),
    affliction: (libelle: string) => poser((p) => ajouterAffliction(p, libelle)),
    retirerAffliction: (i: number) => poser((p) => retirerAffliction(p, i)),
    renommerAffliction: (i: number, libelle: string) =>
      poser((p) => renommerAffliction(p, i, libelle)),
    chiffrerAffliction: (i: number, cible: CibleEcrite, id: string, valeur: number) =>
      poser((p) => chiffrerUneAffliction(p, i, cible, id, valeur)),
    corrigerChiffreAffliction: (i: number, position: number, valeur: number) =>
      poser((p) => corrigerUnChiffreDAffliction(p, i, position, valeur)),
    effacerChiffreAffliction: (i: number, position: number) =>
      poser((p) => effacerUnChiffreDAffliction(p, i, position)),
  }

  /** La part remplie d'une barre, en pourcentage et jamais hors bornes. */
  const part = (valeur: number, total: number) =>
    total > 0 ? Math.max(0, Math.min(100, (valeur / total) * 100)) : 0
</script>

{#if enBase === undefined}
  <p class="note">Ouverture de la fiche…</p>
{:else if enBase === null || !vue || !c}
  <p class="note">Ce personnage n'existe plus.</p>
  <button type="button" class="lien" onclick={() => aller('/')}>Retour à la liste</button>
{:else}
  <header>
    <button type="button" class="lien" onclick={() => aller('/')}>← Mes personnages</button>
    <div class="titrage">
      <h1 class="gothique">{vue.nom}</h1>
      {#if vue.classe}
        <p class="origine">
          <span class="classe">{vue.classe}</span>
          <span class="detail">
            {#if vue.faction}{vue.faction}{/if}{#if vue.region}{' · '}{vue.region}{/if}
          </span>
        </p>
      {/if}
    </div>
  </header>

  <!-- Le bandeau de vitaux suit le défilement, et ne change pas d'un écran à
       l'autre : ce sont les seuls chiffres qu'on veut sous les yeux en
       permanence. -->
  <div class="vitaux-colle">
    <div class="vitaux">
      <div class="vital">
        <span class="etiq">PV <b>{c.pv.valeur}/{c.pv.max}</b></span>
        <span class="mini-barre">
          <i style:width="{part(c.pv.valeur, c.pv.max)}%" style:background="var(--ko)"></i>
        </span>
      </div>
      <div class="vital">
        <span class="etiq">Stress <b>{c.stress.valeur}/{c.stress.max}</b></span>
        <span class="mini-barre">
          <i
            style:width="{part(c.stress.valeur, c.stress.max)}%"
            style:background="var(--stress)"
          ></i>
        </span>
      </div>
      <div class="vital">
        <span class="etiq">Contam. <b>{c.contamination.valeur}/{c.contamination.max}</b></span>
        <span class="mini-barre">
          <i
            style:width="{part(c.contamination.valeur, c.contamination.max)}%"
            style:background="var(--contam)"
          ></i>
        </span>
      </div>
    </div>
  </div>

  <main>
    {#if ecran === 'jets'}
      <Jets
        {vue}
        personnage={enBase.personnage}
        personnageId={enBase.id}
        ouverte={jetEnCours}
        ouvrir={(c) => (jetEnCours = c)}
        version={jetsFaits}
      />
    {:else if ecran === 'sante'}
      <Sante {vue} {c} {ajuster} {gestes} {part} />
    {:else if ecran === 'perso'}
      <Perso
        {vue}
        {ajuster}
        {part}
        personnage={enBase.personnage}
        acheter={(quoi, id) => poser((p) => acheterUneAugmentation(p, quoi, id))}
        acheterMaitrise={(categorie) => poser((p) => acheterUneMaitrise(p, categorie))}
        acheterCapacite={(id) => poser((p) => acheterUneCapacite(p, id))}
        promouvoir={(noeud) => poser((p) => franchirUnRang(p, noeud))}
        basculerNarratif={(texte) => poser((p) => basculerUnNarratif(p, texte))}
        basculerGreffe={(greffe) => poser((p) => basculerUneGreffe(p, greffe))}
        accomplirLeSacrement={(s) => poser((p) => accomplirUnSacrement(p, s))}
        garage={(modele, index) =>
          poser((p) =>
            modele !== null ? ajouterUneMachine(p, modele) : retirerUneMachine(p, index ?? 0),
          )}
        baptiser={(index, nom) => poser((p) => renommerUneMachine(p, index, nom))}
        abimer={(index, subis) => poser((p) => abimerUneMachine(p, index, subis))}
        boulonner={(index, equipement) =>
          poser((p) => basculerEquipementDeMachine(p, index, equipement))}
        forger={(modele, reussi) => poser((p) => forgerUneMain(p, modele, reussi))}
        acheterMain={(modele) => poser((p) => acheterUneMain(p, modele))}
        dessiner={(main, reussi) => poser((p) => forgerUneMainLibre(p, main, reussi))}
        baptiserLaMain={(index, nom) => poser((p) => renommerUneMain(p, index, nom))}
        chargerLaMain={(index, delta) => poser((p) => chargerUneMain(p, index, delta))}
        defaireLaMain={(index) => poser((p) => defaireUneMain(p, index))}
        apprivoiser={(profil) => poser((p) => apprivoiserUnAnimal(p, profil))}
        instruire={(quoi, texte, valeur) =>
          poser((p) => instruireLeCompagnon(p, quoi, texte, valeur))}
        ecrireUnRituel={(nom, cercle, duree, effet) =>
          poser((p) => ecrireUnRituel(p, nom, cercle, duree, effet))}
        effacerUnRituel={(index) => poser((p) => effacerUnRituel(p, index))}
        designerUnFavori={(sphere) => poser((p) => designerUnFavori(p, sphere))}
        choisirUnElement={(element) => poser((p) => choisirUnElement(p, element))}
        jouerUneCarte={(sphere, carte, issue, horsLimite) =>
          poser((p) => jouerUneCarte(p, sphere, carte, issue, horsLimite))}
        racheterUneCarte={(sphere, carte, moyen) =>
          poser((p) => racheterUneCarte(p, sphere, carte, moyen))}
        nouvelleJournee={() => poser((p) => nouvelleJournee(p))}
      />
    {:else}
      <Sac
        {vue}
        personnage={enBase.personnage}
        reparer={(objet, reussi) => poser((p) => tenterUneReparation(p, objet, reussi))}
        ramasser={(objet, payer) => poser((p) => ajouterObjet(p, objet, payer))}
        inscrire={(nom, enc, prix, note) =>
          poser((p) => ajouterObjetLibre(p, nom, enc, prix, note))}
        renforcer={(objet, source, sens) => poser((p) => poserUnRenfort(p, objet, source, sens))}
        ameliorer={(objet, propriete, reussi) =>
          poser((p) => tenterUneAmelioration(p, objet, propriete, reussi))}
        monter={(objet, amelioration) =>
          poser((p) => basculerAmelioration(p, objet, amelioration))}
        attribuer={(objet, propriete) => poser((p) => attribuerUnePropriete(p, objet, propriete))}
        bourse={(delta) => poser((p) => ajusterLaBourse(p, delta))}
        seDefaire={(objet, vendre) => poser((p) => retirerObjet(p, objet, vendre))}
        rangerObjet={(objet: string, ou: Rangement) => poser((p) => rangerObjet(p, objet, ou))}
        reprendre={(nom: string) => poser((p) => reprendreUnObjetLibre(p, nom))}
        renommerObjet={(objet: string, nom: string) =>
          poser((p) => renommerObjetLibre(p, objet, nom))}
        annoterObjet={(objet: string, note: string) =>
          poser((p) => annoterObjetLibre(p, objet, note))}
        chiffrerObjet={(objet: string, cible: CibleEcrite, id: string, valeur: number) =>
          poser((p) => chiffrerUnObjetLibre(p, objet, cible, id, valeur))}
        corrigerChiffreObjet={(objet: string, position: number, valeur: number) =>
          poser((p) => corrigerUnChiffreDObjet(p, objet, position, valeur))}
        effacerChiffreObjet={(objet: string, position: number) =>
          poser((p) => effacerUnChiffreDObjet(p, objet, position))}
        ouvrirJet={(competence) => {
          confrontation = false
          typeDeTest = null
          jetEnCours = competence
        }}
      />
    {/if}
  </main>

  {#if jetEnCours}
    <!-- Remonté à chaque changement de Compétence : le tiroir repart neuf,
         sans traîner les cases cochées du Test précédent. -->
    {#key jetEnCours}
      <TiroirDeJet
        {vue}
        personnage={enBase.personnage}
        personnageId={enBase.id}
        competence={jetEnCours}
        {confrontation}
        declarerConfrontation={(v) => (confrontation = v)}
        {typeDeTest}
        declarerType={(v) => (typeDeTest = v)}
        depenserChance={() => ajuster('chance', -1)}
        apresJet={() => (jetsFaits += 1)}
        fermer={() => (jetEnCours = null)}
        decalage={hauteurBarre}
      />
    {/key}
  {/if}

  <nav
    class="barre-basse"
    aria-label="Écrans de la fiche"
    bind:clientHeight={hauteurBarre}
  >
    <div class="onglets" role="tablist">
      {#each ECRANS as e (e.id)}
        <button
          type="button"
          class="onglet"
          role="tab"
          aria-selected={e.id === ecran}
          onclick={() => (ecran = e.id)}
        >
          {e.nom}
        </button>
      {/each}
    </div>
  </nav>
{/if}

<style>
  header {
    margin-bottom: calc(var(--pas) * 2);
  }
  /* Le nom en gothique, le titre à sa droite : c'est la mise en page de la
     maquette, et celle de la fiche imprimée. */
  .titrage {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: calc(var(--pas) * 4);
    flex-wrap: wrap;
    margin-top: calc(var(--pas) * 2);
  }
  h1 {
    font-size: clamp(1.7rem, 6vw, 2.8rem);
    line-height: 1.1;
    margin: 0;
    color: var(--ink);
  }
  .origine {
    margin: 0;
    display: grid;
    justify-items: end;
    text-align: right;
    gap: 2px;
  }
  .classe {
    color: var(--gold);
    font-family: 'Cinzel', serif;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-size: 0.8rem;
    font-weight: 700;
  }
  .detail {
    color: var(--ink-soft);
    font-size: 0.8rem;
  }

  .vitaux-colle {
    position: sticky;
    top: 0;
    z-index: 2;
    background: var(--bg);
    padding: calc(var(--pas) * 2) 0;
    border-bottom: 1px solid var(--line);
    margin-bottom: calc(var(--pas) * 4);
  }

  /* La navigation reste au pouce, en bas, et ne défile pas. */
  main {
    padding-bottom: calc(var(--pas) * 4);
  }
  .barre-basse {
    position: sticky;
    bottom: 0;
    z-index: 2;
    background: var(--bg);
    padding: calc(var(--pas) * 2) 0;
    border-top: 1px solid var(--line);
  }
  .barre-basse .onglet {
    flex: 1;
    justify-content: center;
  }

  .note {
    color: var(--ink-soft);
  }
  .lien {
    background: none;
    border: none;
    color: var(--ink-soft);
    text-decoration: underline;
    cursor: pointer;
    font: inherit;
    font-size: 0.85rem;
    padding: 0;
  }
</style>
